/**
 * saveHealth — "are this device's edits actually reaching the cloud?"
 *
 * WHY (Walter 2026-08-20). His PC spent a day failing to save every few
 * minutes — one edit sat unsent from 15:20 to 18:15 — and the app said
 * nothing. The console was full of `net::ERR_BLOCKED_BY_CLIENT` on
 * `firestore.googleapis.com/...&zx=`: Brave Shields (he normally runs Chrome
 * + Ghostery) matching Firestore's cache-buster against filter rules written
 * for trackers.
 *
 * We already HAD a detector for this — ExtensionBlockHint — and it stayed
 * silent throughout. It works by probing a Firestore-shaped URL at startup
 * and timing how fast the rejection comes back, which is a prediction about
 * how a blocker behaves. Brave's didn't match the signature.
 *
 * So stop predicting and measure the symptom instead: if saves keep failing,
 * the user needs to know, whatever the cause — extension, proxy, captive
 * portal, or a Firestore outage. This can't be evaded by a blocker we didn't
 * anticipate, because it doesn't look at the blocker at all.
 *
 * Deliberately simple: a burst counter with no timers. A success clears it,
 * so a single flaky write never raises the banner.
 */

// Three failures with no success in between, all within this window. Tuned
// against Walter's real log: his bursts fired 3+ failures inside a minute,
// while ordinary use produces isolated ones at most.
const BURST_SIZE = 3;
const BURST_WINDOW_MS = 3 * 60 * 1000;

let failures = [];        // timestamps, most recent last
let listeners = new Set();

function emit() {
  const failing = isSaveFailing();
  for (const fn of listeners) {
    try { fn(failing); } catch { /* a listener must never break the app */ }
  }
}

/** Record a save that did NOT reach the cloud (it was queued locally). */
export function noteSaveFailure(now = Date.now()) {
  failures.push(now);
  // Keep only what's inside the window; unbounded growth would be silly.
  failures = failures.filter(t => now - t <= BURST_WINDOW_MS);
  emit();
}

/** Record a save that landed. Clears the burst — the path is working again. */
export function noteSaveSuccess() {
  if (failures.length === 0) return;
  failures = [];
  emit();
}

/**
 * True when enough saves have failed recently, with no success since, that
 * the user should be told their edits aren't leaving the device.
 */
export function isSaveFailing(now = Date.now()) {
  const recent = failures.filter(t => now - t <= BURST_WINDOW_MS);
  return recent.length >= BURST_SIZE;
}

export function subscribeSaveHealth(fn) {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** Test seam — resets module state between cases. */
export function __resetSaveHealth() {
  failures = [];
  listeners = new Set();
}
